import { FC, useEffect, useMemo, useRef, useState } from "react";
import { LoadingSpinner } from "@jugl-web/ui-components";
import { cx, useTranslations } from "@jugl-web/utils";
import { ReactComponent as CheckCircle } from "./assets/check-circle.svg";
import loader from "./assets/loader.gif";
import blueSpots from "./assets/blue-spots.png";
import { useTrialLoadingSteps } from "./hooks/useTrialLoadingSteps";

// Static imports for company logos to avoid dynamic require
import company1 from "./assets/company-1.png";
import company2 from "./assets/company-2.png";
import company3 from "./assets/company-3.png";
import company4 from "./assets/company-4.png";
import company5 from "./assets/company-5.png";
import company6 from "./assets/company-6.png";
import company7 from "./assets/company-7.png";
import company8 from "./assets/company-8.png";
import company9 from "./assets/company-9.png";
import company10 from "./assets/company-10.png";
import company11 from "./assets/company-11.png";
import company12 from "./assets/company-12.png";

const companyLogos = [
  company1,
  company2,
  company3,
  company4,
  company5,
  company6,
  company7,
  company8,
  company9,
  company10,
  company11,
  company12,
];

const infiniteLogos = [...companyLogos, ...companyLogos];

export const TrialFullPageLoader: FC<{
  onComplete: () => void;
}> = ({ onComplete }) => {
  const { t } = useTranslations();

  const loadingSteps = useMemo(
    () => [
      {
        loading: t({
          id: "trial.full-page-loader.step1-loading",
          defaultMessage: "Creating test “Jugl” Workspace",
        }),
        done: t({
          id: "trial.full-page-loader.step1-done",
          defaultMessage: "Test “Jugl” Workspace created",
        }),
      },
      {
        loading: t({
          id: "trial.full-page-loader.step-2-loading",
          defaultMessage: "Connecting to Channels",
        }),
        done: t({
          id: "trial.full-page-loader.step2-done",
          defaultMessage: "Channels connected",
        }),
      },
      {
        loading: t({
          id: "trial.full-page-loader.step-3-loading",
          defaultMessage: "Training AI Agent based on given sources",
        }),
        done: t({
          id: "trial.full-page-loader.step3-done",
          defaultMessage: "AI Agent is trained",
        }),
      },
      {
        loading: t({
          id: "trial.full-page-loader.step4-loading",
          defaultMessage: "Almost done...",
        }),
        done: t({
          id: "trial.full-page-loader.step4-done",
          defaultMessage: "Let's Start!",
        }),
      },
    ],
    [t]
  );

  const { activeStepIndex, completedSteps, isAllStepsCompleted } =
    useTrialLoadingSteps(loadingSteps);

  const [translateX, setTranslateX] = useState(0);
  const logosRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let animationFrame: number;
    let start: number | null = null;
    const SPEED = 0.02; // px per ms

    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const newTranslateX = elapsed * SPEED;

      if (logosRef.current) {
        const logosWidth = logosRef.current.scrollWidth / 2; // Width of one set of logos
        if (newTranslateX >= logosWidth) {
          start = timestamp; // Reset for seamless loop
          setTranslateX(0);
        } else {
          setTranslateX(newTranslateX);
        }
      }
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  useEffect(() => {
    if (isAllStepsCompleted) {
      const timer = setTimeout(() => {
        onComplete();
      }, 1000); // Delay for final animation
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isAllStepsCompleted, onComplete]);

  return (
    <div
      className="font-secondary absolute inset-0 z-50 flex min-h-[700px] flex-col items-center justify-start overflow-y-auto bg-no-repeat pt-[20vh]"
      style={{
        backgroundImage: `url(${blueSpots})`,
        backgroundColor: "#1A2023",
      }}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 flex flex-col items-center text-white">
        <img
          src={loader}
          alt="Loading animation"
          className="mb-[58px] h-[63px] w-[72px] rounded-lg object-cover"
        />
        <div className="flex h-[200px] flex-col gap-6">
          {loadingSteps.map((step, index) => {
            const label = completedSteps.has(step.loading)
              ? step.done
              : step.loading;
            const isCompleted = completedSteps.has(step.loading);

            if (index > activeStepIndex) {
              return null;
            }

            return (
              <div
                key={label}
                className={cx(
                  "flex w-[500px] items-center gap-3 text-left text-lg text-white transition-opacity duration-500"
                )}
              >
                <div className="h-8 w-8">
                  {isCompleted ? (
                    <CheckCircle className="h-8 w-8" />
                  ) : (
                    <LoadingSpinner className="h-8 w-8 " color="grey" />
                  )}
                </div>
                <span className="font-secondary whitespace-nowrap">
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="absolute bottom-[72px] z-10 w-full max-w-[min(1000px,90vw)] justify-self-end overflow-hidden">
        <div className="z-10 w-full pb-[32px] text-center text-[24px] font-medium text-white">
          {t({
            id: "trial.full-page-loader.footer",
            defaultMessage:
              "Over 1000+ businesses, from startups to industry leaders use Jugl",
          })}
        </div>
        <div
          ref={logosRef}
          className="flex flex-nowrap"
          style={{ transform: `translateX(-${translateX}px)` }}
        >
          {infiniteLogos.map((logo, index) => (
            <img
              src={logo}
              alt={`Company ${index + 1}`}
              className="mr-6 h-10 shrink-0 object-contain opacity-50"
            />
          ))}
        </div>
      </div>
    </div>
  );
};
